import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { styled } from "@mui/material/styles";
import { fetchReturnsData } from "../../../../Utilities/Url";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Skeleton,
  Stack,
  TableFooter,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { utils, writeFile } from "xlsx";
import { canExportToCSV } from "../config";

const ReturnsTable = () => {
  const returns = useSelector((state) => state.Returns);
  const rowsPerPage = useSelector((state) => state.RowsPerPage);
  const page = useSelector((state) => state.SetPage);
  const searchItem = useSelector((state) => state.SearchItems);
  const selectedExchange = useSelector((state) => state.SetExchange);
  const setActiveButton = useSelector((state) => state.ActiveButton);
  const SelectedDropdownOption = useSelector(
    (state) => state.SelectedDropdownOption
  );
  const isLoading = useSelector((state) => state.isLoading);
  const authToken = useSelector((state) => state.authToken);
  const selectedAnalytics = useSelector((state) => state.selectedAnalytics);

  const [ArrowIcon, setArrowIcon] = useState(true);
  const [selectedColumn, setSelectedColumn] = useState("oneDay");

  const dispatch = useDispatch();

  const [sortConfig, setSortConfig] = useState({
    key: "oneDay",
    direction: "descending",
  });

  useEffect(() => {
    if (SelectedDropdownOption === "Returns") {
      fetchReturnsData(
        dispatch,
        selectedExchange,
        authToken,
        selectedAnalytics
      );
    }
  }, [SelectedDropdownOption, selectedExchange, setActiveButton]);

  const StyledAnchor = styled("a")(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  }));

  const timePeriods = {
    oneDay: 0,
    oneWeek: 1,
    oneMonth: 2,
    threeMonth: 3,
    sixMonth: 4,
    oneYear: 5,
    threeYear: 6,
    fiveYear: 7,
    maxYear: 8,
    yearToDate: 9,
  };
  const LeftAlignTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#ebebeb",
      color: theme.palette.common.black,
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: "2px 4px",
    },
    textAlign: "left",

    [`&.${tableCellClasses.body}`]: {
      fontSize: 12,
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: "2px 4px",
    },
    textAlign: "left",
  }));

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#ebebeb",
      color: theme.palette.common.black,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    textAlign: "right",
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      // backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
      border: 0,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    fontWeight: "normal",
    textAlign: "right",
  }));

  const rowsPerPageOptions = [5, 10, 25];

  const handleChangePage = (event, newPage) => {
    dispatch({ type: "SET_PAGE", payload: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch({
      type: "ROWS_PER_PAGE",
      payload: parseInt(event.target.value, 10),
    });
    dispatch({ type: "SET_PAGE", payload: 0 });
  };

  let filterTicker = returns.filter((ele) =>
    ele.Ticker.includes(searchItem[searchItem.length - 1])
  );

  const handleFirstPageButtonClick = (event) => {
    handleChangePage(event, 0);
  };

  const handleLastPageButtonClick = (event, count) => {
    handleChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  const TablePaginationActions = (props) => {
    const { count, page, rowsPerPage, onPageChange } = props;
    return (
      <div style={{ flexShrink: 0, marginLeft: "2.5em" }}>
        <IconButton
          onClick={(event) => onPageChange(event, 0)}
          disabled={page === 0}
          aria-label="first page"
        >
          <FirstPageIcon />
        </IconButton>
        <IconButton
          onClick={(event) => onPageChange(event, page - 1)}
          disabled={page === 0}
          aria-label="previous page"
        >
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton
          onClick={(event) => onPageChange(event, page + 1)}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton
          onClick={(event) => handleLastPageButtonClick(event, count)}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          <LastPageIcon />
        </IconButton>
      </div>
    );
  };

  const sortedData = [
    ...(filterTicker.length === 0 ? returns : filterTicker),
  ].sort((a, b) => {
    const index = timePeriods[sortConfig.key];
    if (a.Values[index] < b.Values[index]) {
      return sortConfig.direction === "ascending" ? -1 : 1;
    }
    if (a.Values[index] > b.Values[index]) {
      return sortConfig.direction === "ascending" ? 1 : -1;
    }
    return 0;
  });

  const handleSortClick = (key) => {
    setSortConfig((prevConfig) => {
      const newDirection =
        prevConfig.key === key && prevConfig.direction === "descending"
          ? "ascending"
          : "descending";
      return {
        key: key,
        direction: newDirection,
      };
    });

    setSelectedColumn(key);
    setArrowIcon((prev) => !prev);
  };
  const handleExportCSV = () => {
    const headers = [
      "Ticker",
      "1D",
      "1W",
      "1M",
      "3M",
      "6M",
      "1Y",
      "3Y",
      "5Y",
      "Max",
      "YTD",
    ];

    const csvData = sortedData.map((row, index) => {
      return [
        row.Ticker || "",
        row.Values[timePeriods.oneDay],
        row.Values[timePeriods.oneWeek],
        row.Values[timePeriods.oneMonth],
        row.Values[timePeriods.threeMonth],
        row.Values[timePeriods.sixMonth],
        row.Values[timePeriods.oneYear],
        row.Values[timePeriods.threeYear],
        row.Values[timePeriods.fiveYear],
        row.Values[timePeriods.maxYear],
        row.Values[timePeriods.yearToDate],
      ];
    });

    const ws = utils.aoa_to_sheet([headers, ...csvData]);

    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "ReturnsData");

    writeFile(wb, "Returns.xlsx");
  };
  return (
    <div>
      {isLoading ? (
        <Stack spacing={1} padding={"5px"}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            animation="wave"
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={50}
            animation="wave"
          />
          <div style={{ display: "flex", flexDirection: "row", gap: "15px" }}>
            <Skeleton
              variant="rectangular"
              width={300}
              height={100}
              animation="wave"
            />
            <Skeleton
              variant="rectangular"
              width={300}
              height={100}
              animation="wave"
            />
          </div>
        </Stack>
      ) : (
        <TableContainer sx={{ maxHeight: 420 }}>
          {canExportToCSV && (
            <div
              style={{
                display: "flex",
                justifyContent: "end",
                padding: "6px",
                cursor: "pointer",
              }}
            >
              <DownloadIcon
                onClick={handleExportCSV}
                aria-label="export to CSV"
              />
            </div>
          )}
          <Table stickyHeader aria-label="customized table" size="small">
            <TableHead>
              <TableRow sx={{ padding: 1 }}>
                <StyledTableCell sx={{ fontWeight: "bold" }}>
                  SYMBOLS
                </StyledTableCell>
                <StyledTableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor:
                      selectedColumn === "oneDay" ? "#e0e9ff9c" : "inherit",
                    color: selectedColumn === "oneDay" ? "white" : "inherit",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSortClick("oneDay")}
                  >
                    1D
                    {sortConfig.key === "oneDay" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </div>
                </StyledTableCell>
                <StyledTableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor:
                      selectedColumn === "oneWeek" ? "#e0e9ff9c" : "inherit",
                    color: selectedColumn === "oneWeek" ? "white" : "inherit",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSortClick("oneWeek")}
                  >
                    1W
                    {sortConfig.key === "oneWeek" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </div>
                </StyledTableCell>
                <StyledTableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor:
                      selectedColumn === "oneMonth" ? "#e0e9ff9c" : "inherit",
                    color: selectedColumn === "oneMonth" ? "white" : "inherit",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSortClick("oneMonth")}
                  >
                    1M
                    {sortConfig.key === "oneMonth" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </div>
                </StyledTableCell>
                <StyledTableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor:
                      selectedColumn === "threeMonth" ? "#e0e9ff9c" : "inherit",
                    color:
                      selectedColumn === "threeMonth" ? "white" : "inherit",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSortClick("threeMonth")}
                  >
                    3M
                    {sortConfig.key === "threeMonth" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </div>
                </StyledTableCell>
                <StyledTableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor:
                      selectedColumn === "sixMonth" ? "#e0e9ff9c" : "inherit",
                    color: selectedColumn === "sixMonth" ? "white" : "inherit",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSortClick("sixMonth")}
                  >
                    6M
                    {sortConfig.key === "sixMonth" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </div>
                </StyledTableCell>
                <StyledTableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor:
                      selectedColumn === "oneYear" ? "#e0e9ff9c" : "inherit",
                    color: selectedColumn === "oneYear" ? "white" : "inherit",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSortClick("oneYear")}
                  >
                    1Y
                    {sortConfig.key === "oneYear" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </div>
                </StyledTableCell>
                <StyledTableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor:
                      selectedColumn === "threeYear" ? "#e0e9ff9c" : "inherit",
                    color: selectedColumn === "threeYear" ? "white" : "inherit",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSortClick("threeYear")}
                  >
                    3Y
                    {sortConfig.key === "threeYear" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </div>
                </StyledTableCell>
                <StyledTableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor:
                      selectedColumn === "fiveYear" ? "#e0e9ff9c" : "inherit",
                    color: selectedColumn === "fiveYear" ? "white" : "inherit",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSortClick("fiveYear")}
                  >
                    5Y
                    {sortConfig.key === "fiveYear" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </div>
                </StyledTableCell>
                <StyledTableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor:
                      selectedColumn === "maxYear" ? "#e0e9ff9c" : "inherit",
                    color: selectedColumn === "maxYear" ? "white" : "inherit",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSortClick("maxYear")}
                  >
                    Max
                    {sortConfig.key === "maxYear" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </div>
                </StyledTableCell>
                <StyledTableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor:
                      selectedColumn === "yearToDate" ? "#e0e9ff9c" : "inherit",
                    color:
                      selectedColumn === "yearToDate" ? "white" : "inherit",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSortClick("yearToDate")}
                  >
                    YTD
                    {sortConfig.key === "yearToDate" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </div>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <StyledTableRow key={row.Ticker}>
                    <LeftAlignTableCell>
                      <a
                        style={{
                          textDecoration: "none",
                          color: "#007BFF",
                          transition: "color 0.3s ease",
                        }}
                        href={`https://www.google.com/finance/quote/${row.Ticker}:${selectedExchange}?hl=en`}
                        target="_blank"
                      >
                        {row.Ticker}
                      </a>
                    </LeftAlignTableCell>
                    <StyledTableCell
                      sx={{
                        backgroundColor:
                          selectedColumn === "oneDay" ? "#e0e9ff9c" : "inherit",
                        color: row.Values[timePeriods.oneDay] < 0 && "red",
                      }}
                    >
                      {row.Values[timePeriods.oneDay]}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        backgroundColor:
                          selectedColumn === "oneWeek"
                            ? "#e0e9ff9c"
                            : "inherit",
                        color: row.Values[timePeriods.oneWeek] < 0 && "red",
                      }}
                    >
                      {row.Values[timePeriods.oneWeek]}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        backgroundColor:
                          selectedColumn === "oneMonth"
                            ? "#e0e9ff9c"
                            : "inherit",
                        color: row.Values[timePeriods.oneMonth] < 0 && "red",
                      }}
                    >
                      {row.Values[timePeriods.oneMonth]}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        backgroundColor:
                          selectedColumn === "threeMonth"
                            ? "#e0e9ff9c"
                            : "inherit",
                        color: row.Values[timePeriods.threeMonth] < 0 && "red",
                      }}
                    >
                      {row.Values[timePeriods.threeMonth]}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        backgroundColor:
                          selectedColumn === "sixMonth"
                            ? "#e0e9ff9c"
                            : "inherit",
                        color: row.Values[timePeriods.sixMonth] < 0 && "red",
                      }}
                    >
                      {row.Values[timePeriods.sixMonth]}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        backgroundColor:
                          selectedColumn === "oneYear"
                            ? "#e0e9ff9c"
                            : "inherit",
                        color: row.Values[timePeriods.oneYear] < 0 && "red",
                      }}
                    >
                      {row.Values[timePeriods.oneYear]}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        backgroundColor:
                          selectedColumn === "threeYear"
                            ? "#e0e9ff9c"
                            : "inherit",
                        color: row.Values[timePeriods.threeYear] < 0 && "red",
                      }}
                    >
                      {row.Values[timePeriods.threeYear]}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        backgroundColor:
                          selectedColumn === "fiveYear"
                            ? "#e0e9ff9c"
                            : "inherit",
                        color: row.Values[timePeriods.fiveYear] < 0 && "red",
                      }}
                    >
                      {row.Values[timePeriods.fiveYear]}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        backgroundColor:
                          selectedColumn === "maxYear"
                            ? "#e0e9ff9c"
                            : "inherit",
                        color: row.Values[timePeriods.maxYear] < 0 && "red",
                      }}
                    >
                      {row.Values[timePeriods.maxYear]}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        backgroundColor:
                          selectedColumn === "yearToDate"
                            ? "#e0e9ff9c"
                            : "inherit",
                        color: row.Values[timePeriods.yearToDate] < 0 && "red",
                      }}
                    >
                      {row.Values[timePeriods.yearToDate]}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  colSpan={12}
                  count={sortedData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      "aria-label": "rows per page",
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default ReturnsTable;
